(function() {
  'use strict';

  // Configuration
  const API_ENDPOINT = 'https://x.fisher-co.com/c';
  const RESOURCE_TIMING_THRESHOLD = 500; // ms

  // State
  const metricsQueue = new Set();
  let sessionId = null;
  let hasFlushed = false;

  // === SESSION ID GENERATION ===
  function generateSessionId() {
    // Check sessionStorage first (persist until tab close)
    const stored = sessionStorage.getItem('rum_session_id');
    if (stored) return stored;

    // Generate new UUID v4
    const id = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });

    sessionStorage.setItem('rum_session_id', id);
    return id;
  }

  // === DEVICE & CONTEXT DETECTION ===
  function getDeviceType() {
    const ua = navigator.userAgent;
    const width = window.innerWidth;

    // Tablet detection
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      return 'tablet';
    }
    // Mobile detection (combine UA + screen size)
    if (/Mobile|iP(hone|od)|Android|BlackBerry|IEMobile/i.test(ua) || width <= 768) {
      return 'mobile';
    }
    return 'desktop';
  }

  function getPageCount() {
    var count = parseInt(sessionStorage.getItem('rum_page_count') || '0') + 1;
    sessionStorage.setItem('rum_page_count', count);
    return count;
  }

  function getUserContext() {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    return {
      url: window.location.href,
      userAgent: navigator.userAgent,
      deviceType: getDeviceType(),
      connectionType: connection ? connection.effectiveType : null,
      deviceMemory: navigator.deviceMemory || null,
      pageType: document.documentElement.getAttribute('data-page-type') || null,
      themeId: parseInt(document.documentElement.getAttribute('data-theme-id')) || null,
      referrer: document.referrer || null,
      pageCount: getPageCount(),
      timestamp: new Date().toISOString()
    };
  }

  // === WEB VITALS CALLBACKS ===
  function onMetric(metric) {
    const context = getUserContext();

    metricsQueue.add({
      sessionId: sessionId,
      metricType: metric.name,
      value: metric.value,
      rating: metric.rating,
      attribution: metric.attribution || {},
      ...context
    });
  }

  // === RESOURCE TIMING COLLECTION ===
  function collectSlowResources() {
    if (!window.performance || !window.performance.getEntriesByType) return [];

    const resources = performance.getEntriesByType('resource');
    const slowResources = resources
      .filter(r => r.duration > RESOURCE_TIMING_THRESHOLD)
      .map(r => ({
        sessionId: sessionId,
        resourceUrl: r.name,
        duration: Math.round(r.duration),
        size: r.transferSize || 0,
        resourceType: r.initiatorType,
        timestamp: new Date().toISOString()
      }));

    return slowResources;
  }

  // === NAVIGATION TIMING COLLECTION ===
  function collectNavigationTiming() {
    if (!window.performance || !window.performance.timing) return null;

    const timing = performance.timing;
    const navigation = performance.navigation;

    return {
      sessionId: sessionId,
      metricType: 'NAVIGATION',
      value: 0, // Not a single value metric
      rating: 'unknown',
      attribution: {
        domInteractive: timing.domInteractive - timing.navigationStart,
        domContentLoaded: timing.domContentLoadedEventEnd - timing.navigationStart,
        loadComplete: timing.loadEventEnd - timing.navigationStart,
        fetchStart: timing.fetchStart - timing.navigationStart,
        responseStart: timing.responseStart - timing.navigationStart,
        responseEnd: timing.responseEnd - timing.navigationStart,
        navigationType: navigation.type // 0=navigate, 1=reload, 2=back/forward
      },
      ...getUserContext()
    };
  }

  // === DATA TRANSMISSION ===
  function flushMetrics() {
    if (hasFlushed || metricsQueue.size === 0) return;

    const metrics = Array.from(metricsQueue);
    const slowResources = collectSlowResources();
    const navTiming = collectNavigationTiming();

    // Add navigation timing if available
    if (navTiming) metrics.push(navTiming);

    const payload = JSON.stringify({
      metrics: metrics,
      resources: slowResources
    });

    // Try sendBeacon first (recommended for page unload)
    // Send as Blob with text/plain to avoid CORS preflight (application/json triggers
    // OPTIONS preflight which races with page close and loses). Server accepts both types.
    const blob = new Blob([payload], { type: 'text/plain' });
    const sent = navigator.sendBeacon(API_ENDPOINT, blob);

    // Fallback to fetch without keepalive if sendBeacon fails
    if (!sent) {
      fetch(API_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload
      }).catch(() => {}); // Silent fail - analytics shouldn't break UX
    }

    hasFlushed = true;
  }

  // === INITIALIZATION ===
  function init() {
    sessionId = generateSessionId();

    // Register web-vitals callbacks (use webVitals global from IIFE build)
    if (typeof webVitals !== 'undefined') {
      webVitals.onLCP(onMetric);
      webVitals.onINP(onMetric);
      webVitals.onCLS(onMetric);
      webVitals.onFCP(onMetric);
      webVitals.onTTFB(onMetric);
    }

    // Flush on page hide (most reliable across mobile/desktop)
    document.addEventListener('visibilitychange', function() {
      if (document.visibilityState === 'hidden') {
        flushMetrics();
      }
    });

    // Fallback flush on pagehide (for older browsers)
    window.addEventListener('pagehide', flushMetrics);
  }

  // Start tracking
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
